import { openPropositionDialog } from "~/util/premium";
import { node, createPremiumIcon } from "~/util/dom";
import createLinkButton from "~/ui/linkbutton";
import { _ } from "~/i18n";
import { DEFAULT_VALUE_USER_SPEED } from "~/util/constants";
import { IUserSpeedData } from "~/state/user/interfaces";
import { ITypeItems, CHANGED_CLASS } from "~/profile/settings/userspeed/popup";
import { createEditContainer, roundToTwoDecimals } from "~/profile/settings/userspeed/popup/utils";

interface IUserspeedTypeItem {
	criterions: Array<number>;
	title: string;
	default: number;
	analyticsType: string;
};

export const USER_SPEED_TYPE_ITEMS: Array<IUserspeedTypeItem> = [
	{
		criterions: [131, 132],
		title: _("profile.settings.userspeed.type1"),
		default: DEFAULT_VALUE_USER_SPEED[131].value,
		analyticsType: "pesky",
	},
	{
		criterions: [134],
		title: _("profile.settings.userspeed.type11"),
		default: DEFAULT_VALUE_USER_SPEED[134].value,
		analyticsType: "beh",
	},
	{
		criterions: [133],
		title: _("profile.settings.userspeed.type10"),
		default: DEFAULT_VALUE_USER_SPEED[133].value,
		analyticsType: "sportovniKocarek",
	},
	{
		criterions: [121],
		title: _("profile.settings.userspeed.type6"),
		default: DEFAULT_VALUE_USER_SPEED[121].value,
		analyticsType: "nakole",
	},
	{
		criterions: [123],
		title: _("profile.settings.userspeed.type2"),
		default: DEFAULT_VALUE_USER_SPEED[121].value,
		analyticsType: "nakoleHorske",
	},
	{
		criterions: [122],
		title: _("profile.settings.userspeed.type3"),
		default: DEFAULT_VALUE_USER_SPEED[122].value,
		analyticsType: "nakoleSilnicni",
	},
	{
		criterions: [124],
		title: _("profile.settings.userspeed.type7"),
		default: DEFAULT_VALUE_USER_SPEED[124].value,
		analyticsType: "gravel",
	},
	{
		criterions: [125],
		title: _("profile.settings.userspeed.type8"),
		default: DEFAULT_VALUE_USER_SPEED[125].value,
		analyticsType: "sportovniKolobezka",
	},
	{
		criterions: [141],
		title: _("profile.settings.userspeed.type4"),
		default: DEFAULT_VALUE_USER_SPEED[141].value,
		analyticsType: "bezky",
	},
	{
		criterions: [142],
		title: _("profile.settings.userspeed.type9"),
		default: DEFAULT_VALUE_USER_SPEED[142].value,
		analyticsType: "skialpy",
	},
	{
		criterions: [144, 143],
		title: _("profile.settings.userspeed.type5"),
		default: DEFAULT_VALUE_USER_SPEED[144].value,
		analyticsType: "vodactvi",
	},
];

export function getAnalyticsType(criterion) {
	let type = "";

	USER_SPEED_TYPE_ITEMS.forEach(item => {
		if (item.criterions.includes(criterion)) {
			type = item.analyticsType;
		}
	});

	return type;
}

export function createSectionTitle(callbackOnNoPremium: () => void, premiumUser): HTMLDivElement {
	const sectionTitle = node("div", {});
	const userSpeedTitle = node("h5", {}, _("profile.settings.userspeed.section.title"));
	const editButton = createLinkButton({
		innerHTML: `${_("set")}`,
	});

	sectionTitle.append(userSpeedTitle);

	if (!premiumUser) {
		editButton.addEventListener("click", () => {
			callbackOnNoPremium();
			Mapy.getComponent("logger").log("setCustomSpeeds_premiumPurchase_show");
			openPropositionDialog("setCustomSpeeds");
		});

		sectionTitle.append(editButton);

		editButton.prepend(createPremiumIcon());
	}

	return sectionTitle;
}

export function userSpeedValidation(editItems:Array<ITypeItems>, validationCallback: (valid?: boolean) => void = () => {}, itemIndex: number = 0) {
	const from = itemIndex;
	const to = itemIndex === 0 ? editItems.length-1 : itemIndex;
	let error = false;

	if (from <= to) {
		for (let index = from; index < editItems.length; index++) {
			const item = editItems[index].editValue;
			const actualValue = parseFloat(item.input.value);
			const changed = actualValue !== USER_SPEED_TYPE_ITEMS[index].default;

			item.container.classList[changed ? "add" : "remove"](CHANGED_CLASS);

			if (changed) {
				item.container.prepend(item.reset);
			} else {
				item.reset.remove();
			}

			if (!error && !item.input.checkValidity()) {
				error = true;
			}
		}
	}

	validationCallback(error);
}

export function createList(settingsUserSpeedValue: IUserSpeedData, validationCallback: (valid: boolean) => void, premium = false) {
	const container = node("ul");
	const editItems: Array<ITypeItems> = [];

	USER_SPEED_TYPE_ITEMS.forEach(item => {
		const itemContainer = node("li");
		const title = node("div", {}, item.title);
		const textValue = node("div");
		const isSetValue = premium && settingsUserSpeedValue && settingsUserSpeedValue[item.criterions[0].toString()];
		const editValue = createEditContainer(
			isSetValue
				? settingsUserSpeedValue[item.criterions[0].toString()]?.value
				: item.default,
			item.default,
			"km/h",
			() => {
				userSpeedValidation(editItems, validationCallback);
			},
		);

		textValue.append(
			isSetValue
				? roundToTwoDecimals(settingsUserSpeedValue[item.criterions[0].toString()]?.value).toString()
				: item.default.toString(),
			" ",
			node("i", {}, "km/h"),
		);

		itemContainer.append(title, textValue);

		editItems.push(
			{
				container: itemContainer,
				textValue,
				editValue,
			},
		);

		editValue.input.addEventListener("input", () => {
			userSpeedValidation(editItems, validationCallback);
		});

		container.append(itemContainer);
	});

	userSpeedValidation(editItems, validationCallback);

	return {
		container,
		editItems,
	};
}

export function showEditUserSpeed(items, show) {
	if (show) {
		items.forEach(item => {
			item.textValue.replaceWith(item.editValue.container);
		});
	} else {
		items.forEach(item => {
			item.editValue.container.replaceWith(item.textValue);
		});
	}
}

export function getUserSpeedData(items: Array<ITypeItems>) {
	const userSpeed: IUserSpeedData = {};

	USER_SPEED_TYPE_ITEMS.forEach((item, key) => {
		const editValue = items[key].editValue;

		if (editValue.container.classList.contains(CHANGED_CLASS)) {
			item.criterions.forEach(criterion => {
				userSpeed[criterion] = {
					value: parseFloat(editValue.input.value) || item.default,
				};
			});
		}
	});

	return userSpeed;
}

export function setUserSpeedValue(settingsUserSpeedValue: IUserSpeedData, items: Array<ITypeItems>) {
	USER_SPEED_TYPE_ITEMS.forEach((item, key) => {
		const value = settingsUserSpeedValue[item.criterions[0].toString()]?.value || item.default;

		items[key].textValue.innerHTML = "";
		items[key].textValue.append(value.toString(), " ", node("i", {}, "km/h"));

		items[key].editValue.input.value = value.toString();
	});
}

export function destroyUserSpeed(items: Array<ITypeItems>) {
	USER_SPEED_TYPE_ITEMS.forEach((item, key) => {
		items[key].editValue.tooltip.destroy();
	});
}
